import {ApplicationConfig, ErrorHandler, isDevMode, provideZoneChangeDetection} from '@angular/core';
import {provideRouter, RouteReuseStrategy, withComponentInputBinding} from '@angular/router';
import {routes} from './app.routes';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideState, provideStore} from '@ngrx/store';
import {projectReducer} from './modules/project/service/project.reducer';
import {provideEffects} from '@ngrx/effects';
import {ProjectEffects} from './modules/project/service/project.effects';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {LoadingInterceptor} from './utils/interceptors/loading.interceptor';
import {ErrorInterceptor} from './utils/interceptors/error.interceptor';
import {researchKeywordReducer} from './modules/research/services/research-keyword/research-keyword.reducer';
import {ResearchKeywordEffects} from './modules/research/services/research-keyword/research-keyword.effects';
import {ScreeningCriteriaEffects} from './modules/research/services/screening-criteria/screening-criteria.effects';
import {screeningCriteriaReducer} from './modules/research/services/screening-criteria/screening-criteria.reducer';
import {screeningSettingReducer} from './modules/research/services/screening-settings/screening-settings.reducer';
import {ScreeningSettingEffects} from './modules/research/services/screening-settings/screening-settings.effects';
import {researchPaperReducer} from './modules/research/services/research-paper/research-paper.reducer';
import {ResearchPaperEffects} from './modules/research/services/research-paper/research-paper.effects';
import {ProcessInfoEffects} from './modules/process-info/services/process-info.effects';
import {processInfoReducer} from './modules/process-info/services/process-info.reducer';
import {PaperChatEffects} from './modules/chat/service/paper-chat.effects';
import {paperChatReducer} from './modules/chat/service/paper-chat.reducer';
import {GlobalErrorHandler} from './utils/interceptors/error.handler';
import {FileDownloadEffects} from './modules/research/services/download/file-download.effects';
import {embeddingReducer} from './modules/chat/service/embedding.reducer';
import {EmbeddingEffects} from './modules/chat/service/embedding.effects';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {provideRouterStore, routerReducer} from '@ngrx/router-store';
import {HttpHeaderInterceptor} from './utils/interceptors/http-header.interceptor';
import {SummaryEffects} from './modules/summary/services/summary/summary.effects';
import {summaryReducer} from './modules/summary/services/summary/summary.reducer';
import {provideNgcCookieConsent} from 'ngx-cookieconsent';
import {cookieConfig} from './utils/models/cookie';
import {CustomReuseStrategy} from './utils/routing/custom-reuse-strategy';
import {snapshotReducer} from './modules/snapshot/services/snapshot.reducer';
import {SnapshotEffects} from './modules/snapshot/services/snapshot.effects';
import {provideUiTour} from 'ngx-ui-tour-md-menu';
import {globalConfigReducer} from './modules/global-config/global-config.reducer';
import {GlobalConfigEffects} from './modules/global-config/global-config.effects';


export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideNgcCookieConsent(cookieConfig),
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    provideRouter(
      routes,
      withComponentInputBinding()
    ),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpHeaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {provide: RouteReuseStrategy, useClass: CustomReuseStrategy},
    provideUiTour({
      enableBackdrop: true,
      backdropConfig: {
        offset: 10
      }
    }),
    provideStore(),
    provideState({name: 'projects', reducer: projectReducer}),
    provideState({name: 'embeddings', reducer: embeddingReducer}),
    provideState({name: 'researchKeywords', reducer: researchKeywordReducer}),
    provideState({name: 'screeningCriteria', reducer: screeningCriteriaReducer}),
    provideState({name: 'screeningSetting', reducer: screeningSettingReducer}),
    provideState({name: 'researchPapers', reducer: researchPaperReducer}),
    provideState({name: 'processInfo', reducer: processInfoReducer}),
    provideState({name: 'paperChat', reducer: paperChatReducer}),
    provideState({name: 'router', reducer: routerReducer}),
    provideState({name: 'summary', reducer: summaryReducer}),
    provideState({name: 'snapshot', reducer: snapshotReducer}),
    provideState({name: 'globalConfig', reducer: globalConfigReducer}),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true // If set to true, the connection is established within the Angular zone
    }),
    provideEffects([
      ProjectEffects,
      ResearchKeywordEffects,
      ScreeningCriteriaEffects,
      ScreeningSettingEffects,
      ResearchPaperEffects,
      ProcessInfoEffects,
      PaperChatEffects,
      FileDownloadEffects,
      EmbeddingEffects,
      SummaryEffects,
      SnapshotEffects,
      GlobalConfigEffects
    ]),
    provideRouterStore(),
  ]
};
